/**
 * Encapsulates ThreatCat "send finding" flow: connection modal state,
 * send/error tracking per question, and the actual direct push call.
 */

import { useState, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useQuestions } from '@/contexts/QuestionsContext';
import { useThreatCat } from '@/contexts/ThreatCatContext';
import { useProject } from '@/contexts/ProjectContext';
import { useDiagramData } from '@/hooks/useDiagramData';
import { useWriteBackLock } from '@/hooks/useWriteBackLock';
import { threatcatService } from '@/services/threatcatService';
import type { VulnerabilityFinding } from '@/types/ai';
import type { Threat } from '@/types/threat';
import { ThreatCatAuthError } from '@/lib/errors';

export interface ThreatCatSendState {
  pendingSendFinding: VulnerabilityFinding | null;
  connectionOpen: boolean;
  confirmationOpen: boolean;
  preflightLoading: boolean;
  preflightError: string | null;
  sendingQuestionIds: Set<number>;
  sendResults: Record<number, 'success' | 'error'>;
  sendErrors: Record<number, string>;
  writeBackLockMessage: string | null;
}

export interface ThreatCatSendActions {
  handleSendToThreatCat: (finding: VulnerabilityFinding) => void;
  setConnectionOpen: (open: boolean) => void;
  setConfirmationOpen: (open: boolean) => void;
  confirmSend: () => void;
  cancelSend: () => void;
  onConnected: () => void;
}

export function useThreatCatSend() {
  const { threats, setAppState } = useApp();
  const { questions, invalidateQuestions } = useQuestions();
  const { cookie, getCookie, disconnect } = useThreatCat();
  const { detail } = useProject();
  const { isLocked: isWriteBackLocked, message: writeBackLockMessage } = useWriteBackLock();
  const effectiveVersionId = detail?.project_version_id ? String(detail.project_version_id) : null;
  const { currentDiagram } = useDiagramData();

  const [pendingSendFinding, setPendingSendFinding] = useState<VulnerabilityFinding | null>(null);
  const [connectionOpen, setConnectionOpen] = useState(false);
  const [sendingQuestionIds, setSendingQuestionIds] = useState<Set<number>>(new Set());
  const [sendResults, setSendResults] = useState<Record<number, 'success' | 'error'>>({});
  const [sendErrors, setSendErrors] = useState<Record<number, string>>({});

  const doSendToThreatCat = useCallback(async (
    finding: VulnerabilityFinding,
    projectId: string,
    activeCookie: string,
  ) => {
    const qid = finding.question_id;
    if (isWriteBackLocked) {
      setSendErrors((prev) => ({ ...prev, [qid]: writeBackLockMessage ?? 'Project version is locked in ThreatCat' }));
      setSendResults((prev) => ({ ...prev, [qid]: 'error' }));
      return;
    }
    const matchingQuestion = questions.find((q) => q.id === qid);
    const vulnId = finding.vulnerability_id ?? matchingQuestion?.vulnerabilities?.[0]?.id;
    if (!vulnId) {
      setSendErrors((prev) => ({ ...prev, [qid]: 'No persisted vulnerability to push' }));
      setSendResults((prev) => ({ ...prev, [qid]: 'error' }));
      return;
    }
    setSendingQuestionIds((prev) => new Set(prev).add(qid));
    setSendResults((prev) => { const s = { ...prev }; delete s[qid]; return s; });
    setSendErrors((prev) => { const s = { ...prev }; delete s[qid]; return s; });
    try {
      await threatcatService.pushToThreatCat({
        project_version_id: projectId,
        cookie: activeCookie,
        vulnerability_id: vulnId,
      });
      const newThreat: Threat = {
        name: finding.vulnerability_title ?? '',
        scenario: finding.threat_scenario ?? '',
        vulnerability: finding.vulnerability_description ?? '',
        countermeasure: finding.countermeasure ?? '',
        status: 'open',
        type: 'threat_modeling',
        rating_vector_string: finding.rating_vector_string ?? '',
        diagram_id: currentDiagram?.id,
      };
      setAppState({ threats: [...threats, newThreat] });
      // Reload questions to get updated threatcat_threat_id
      await invalidateQuestions();
      setSendResults((prev) => ({ ...prev, [qid]: 'success' }));
    } catch (err) {
      if (err instanceof ThreatCatAuthError) disconnect();
      setSendErrors((prev) => ({ ...prev, [qid]: err instanceof Error ? err.message : 'Failed to push to ThreatCat' }));
      setSendResults((prev) => ({ ...prev, [qid]: 'error' }));
    } finally {
      setSendingQuestionIds((prev) => { const s = new Set(prev); s.delete(qid); return s; });
    }
  }, [questions, currentDiagram, threats, setAppState, invalidateQuestions, disconnect, isWriteBackLocked, writeBackLockMessage]);

  const handleSendToThreatCat = useCallback((finding: VulnerabilityFinding) => {
    if (isWriteBackLocked) {
      const qid = finding.question_id;
      setSendErrors((prev) => ({ ...prev, [qid]: writeBackLockMessage ?? 'Project version is locked in ThreatCat' }));
      setSendResults((prev) => ({ ...prev, [qid]: 'error' }));
      return;
    }
    if (!cookie || !effectiveVersionId) {
      setPendingSendFinding(finding);
      setConnectionOpen(true);
      return;
    }
    void doSendToThreatCat(finding, effectiveVersionId, cookie);
  }, [cookie, effectiveVersionId, doSendToThreatCat, isWriteBackLocked, writeBackLockMessage]);

  const cancelSend = useCallback(() => {
    setConnectionOpen(false);
    setPendingSendFinding(null);
  }, []);

  const onConnected = useCallback(() => {
    setConnectionOpen(false);
    if (isWriteBackLocked) {
      setPendingSendFinding(null);
      return;
    }
    const activeCookie = getCookie() || cookie;
    if (pendingSendFinding && effectiveVersionId && activeCookie) {
      const finding = pendingSendFinding;
      setPendingSendFinding(null);
      void doSendToThreatCat(finding, effectiveVersionId, activeCookie);
    }
  }, [cookie, doSendToThreatCat, effectiveVersionId, getCookie, isWriteBackLocked, pendingSendFinding]);

  return {
    state: {
      pendingSendFinding,
      connectionOpen,
      confirmationOpen: false,
      preflightLoading: false,
      preflightError: null,
      sendingQuestionIds,
      sendResults,
      sendErrors,
      writeBackLockMessage,
    },
    actions: {
      handleSendToThreatCat,
      setConnectionOpen,
      setConfirmationOpen: () => {},
      confirmSend: () => {},
      cancelSend,
      onConnected,
    },
  };
}
